import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import {
    Step,
    StepList,
    StepPanel,
    StepPanels,
    Stepper,
} from 'primeng/stepper';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';
import {
    AdminGame,
    CategoryType,
    DisplayInfo,
    GameTemplateWithStatus,
    GameVariantTemplate,
    ProviderStatus,
    WinMode,
} from '@/lib-backend';
import { GameTemplatesService } from '@/app/services/game-templates.service';
import { RealmGamesService } from '@/app/services/realm-games.service';
import { RealmGameVariantsService } from '@/app/services/realm-game-variants.service';
import { FakeReviewService } from '@/app/services/fake-review.service';
import { NotificationService } from '@/app/services/notification.service';
import {
    gameModeLabel,
    winModeLabel,
    withResolvedGameKind,
} from '@/app/utils/game';
import { resolveGameContentUrl } from '@/app/utils/game-content-url';
import {
    modeConfigTypeLabel,
    variantEntryFee,
    variantPlayerCount,
    variantTopReward,
} from '@/app/utils/variant';
import { OverrideField } from '@/app/models/override-field.model';
import { FundsDisplay } from '@/app/components/funds-display';
import { ReviewDistributionForm } from './fake-reviews/review-distribution-form';
import { RealmGameArtPanel } from './realm-game-art-panel';
import {
    buildBellDistribution,
    resolveGameAutoTags,
} from './fake-reviews/review-distribution';

const SEED_RANGE_DAYS = 30;

interface GameDraft {
    Title: OverrideField<string>;
    Description: OverrideField<string>;
    IsActive: OverrideField<boolean>;
    IsFeatured: OverrideField<boolean>;
}

interface VariantDraft {
    overrideDisplayInfo: boolean;
    displayName: string;
    displayDescription: string;
    displayImageUrl: string;
    overrideIsActive: boolean;
    isActive: boolean;
}

@Component({
    selector: 'app-add-game-wizard',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Checkbox,
        Dialog,
        InputText,
        Message,
        Tag,
        Textarea,
        Stepper,
        StepList,
        Step,
        StepPanels,
        StepPanel,
        FundsDisplay,
        ReviewDistributionForm,
        RealmGameArtPanel,
    ],
    templateUrl: './add-game-wizard.html',
})
export class AddGameWizard implements OnInit {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }
    @Input({ required: true }) realmId = '';
    /** Realm category type; only templates whose game-kind WinMode matches are offered. */
    readonly categoryType = input.required<CategoryType>();
    /** The realm's site template id — keys the created game's art (variants/AI). */
    readonly siteTemplateId = input<string | null>(null);

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() created = new EventEmitter<void>();

    protected readonly templatesService = inject(GameTemplatesService);
    protected readonly gamesService = inject(RealmGamesService);
    protected readonly variantsService = inject(RealmGameVariantsService);
    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly notification = inject(NotificationService);

    protected readonly visibleSignal = signal(false);

    // 1: pick game template, 2: configure game, 3: pick variant template,
    // 4: configure variant, 5: seed fake reviews (optional).
    protected readonly step = signal<number>(1);

    protected readonly selectedGameTemplate =
        signal<GameTemplateWithStatus | null>(null);
    protected readonly gameDraft = signal<GameDraft>(this.makeEmptyGameDraft());
    protected readonly createdGame = signal<AdminGame | null>(null);

    protected readonly selectedVariantTemplate =
        signal<GameVariantTemplate | null>(null);
    protected readonly variantDraft = signal<VariantDraft>(
        this.makeEmptyVariantDraft(),
    );

    protected readonly working = signal(false);
    protected readonly stepError = signal<string | null>(null);

    // ---- Step 5: seed fake reviews (optional) ----
    protected readonly seedTargetAverage = signal(4.5);
    protected readonly seedCount = signal(10);
    protected readonly seedConcentration = signal(1);
    protected readonly seedFrom = signal<Date | null>(null);
    protected readonly seedTo = signal<Date | null>(null);
    protected readonly seedError = signal<string | null>(null);

    protected readonly seedTags = computed(() =>
        resolveGameAutoTags(this.createdGame()),
    );

    protected readonly seedDisabled = computed(() => {
        if (this.working()) return true;
        if (this.seedCount() <= 0) return true;
        const t = this.seedTargetAverage();
        if (!Number.isFinite(t) || t < 1 || t > 5) return true;
        if (this.seedTags().length === 0) return true;
        const from = this.seedFrom();
        const to = this.seedTo();
        if (!from || !to || from > to) return true;
        return false;
    });

    /**
     * The game catalog has no category field; WinMode is the category bridge
     * (SkillBased ↔ SkillBasedGames, ChanceBased ↔ LuckyBasedGames). The
     * backend does not enforce the match yet (PBE tech-debt task), so filter
     * client-side. Templates without a resolved GameKind are excluded — their
     * WinMode cannot be verified.
     */
    private readonly wantedWinMode = computed(() =>
        this.categoryType() === CategoryType.SkillBasedGames
            ? WinMode.SkillBased
            : WinMode.ChanceBased,
    );

    protected readonly eligibleTemplates = computed(() =>
        this.templatesService
            .templates()
            .filter(
                (t) =>
                    t.Template.IsActive &&
                    t.ProviderStatus === ProviderStatus.Ok &&
                    t.Template.GameKind?.WinMode === this.wantedWinMode(),
            ),
    );

    protected readonly canSaveGameStep = computed(
        () => !!this.selectedGameTemplate(),
    );

    protected readonly canSaveVariantStep = computed(() => {
        const d = this.variantDraft();
        if (!this.selectedVariantTemplate()) return false;
        if (d.overrideDisplayInfo && !d.displayName.trim()) return false;
        return true;
    });

    protected readonly gameModeLabel = gameModeLabel;
    protected readonly winModeLabel = winModeLabel;
    protected readonly resolveGameContentUrl = resolveGameContentUrl;
    protected readonly modeConfigTypeLabel = modeConfigTypeLabel;
    protected readonly variantPlayerCount = variantPlayerCount;
    protected readonly variantEntryFee = variantEntryFee;
    protected readonly variantTopReward = variantTopReward;

    constructor() {
        effect(() => {
            if (this.visibleSignal()) {
                this.resetAll();
                this.templatesService.ensureTemplatesLoaded();
            }
        });
    }

    ngOnInit(): void {
        this.templatesService.ensureTemplatesLoaded();
    }

    // ---- Step 1: pick game template ----

    protected pickGameTemplate(t: GameTemplateWithStatus): void {
        this.selectedGameTemplate.set(t);
        this.gameDraft.set(this.makeEmptyGameDraft());
        this.stepError.set(null);
        this.step.set(2);
    }

    protected trackGameTemplate(
        _index: number,
        t: GameTemplateWithStatus,
    ): string {
        return t.Template.Id;
    }

    // ---- Step 2: configure game ----

    protected toggleGameOverride<K extends keyof GameDraft>(
        key: K,
        value: boolean,
    ): void {
        const t = this.selectedGameTemplate();
        if (!t) return;
        this.gameDraft.update((d) => {
            const next = { ...d };
            if (value && !d[key].override) {
                next[key] = {
                    override: true,
                    value: this.gameTemplateFieldValue(t, key),
                } as GameDraft[K];
            } else {
                next[key] = { ...d[key], override: value } as GameDraft[K];
            }
            return next;
        });
    }

    protected setGameOverrideValue<K extends keyof GameDraft>(
        key: K,
        value: GameDraft[K]['value'],
    ): void {
        this.gameDraft.update((d) => ({
            ...d,
            [key]: { ...d[key], value } as GameDraft[K],
        }));
    }

    protected gameTemplatePreview(key: keyof GameDraft): string {
        const t = this.selectedGameTemplate()?.Template;
        if (!t) return '';
        switch (key) {
            case 'Title':
                return t.Title;
            case 'Description':
                return t.Description;
            case 'IsActive':
                return t.IsActive ? 'Yes' : 'No';
            case 'IsFeatured':
                return t.IsFeatured ? 'Yes' : 'No';
        }
    }

    protected backToStep1(): void {
        if (this.createdGame() || this.working()) return;
        this.selectedGameTemplate.set(null);
        this.stepError.set(null);
        this.step.set(1);
    }

    protected async createGameAndContinue(): Promise<void> {
        const t = this.selectedGameTemplate();
        if (!t || this.working() || !this.canSaveGameStep()) return;
        if (this.createdGame()) return; // already created — art is configured below
        this.working.set(true);
        this.stepError.set(null);
        try {
            const d = this.gameDraft();
            const result = await this.gamesService.createGame({
                RealmId: this.realmId,
                TemplateId: t.Template.Id,
                Title: d.Title.override ? d.Title.value : null,
                Description: d.Description.override
                    ? d.Description.value
                    : null,
                // Art inherits the template at creation; it is configured on this
                // step via the art panel (which applies immediately) once created.
                BannerUrl: null,
                IconUrl: null,
                Images: null,
                IsActive: d.IsActive.override ? d.IsActive.value : null,
                IsFeatured: d.IsFeatured.override ? d.IsFeatured.value : null,
            });
            if (result.kind === 'ok') {
                // createGame doesn't resolve GameKind; borrow it from the picked
                // template so the art panel can resolve the compatible category.
                this.createdGame.set(
                    withResolvedGameKind(result.game, t.Template.GameKind),
                );
                await this.variantsService.loadTemplates(
                    this.realmId,
                    result.game.Id,
                );
                // Stay on step 2 — the art panel now appears for the created game.
            } else {
                this.stepError.set(this.formatCreateGameError(result.reason));
            }
        } finally {
            this.working.set(false);
        }
    }

    /** Reflect an art change from the panel, keeping the resolved GameKind. */
    protected onGameArtUpdated(g: AdminGame): void {
        this.createdGame.set(
            withResolvedGameKind(
                g,
                this.createdGame()?.Template.Template.GameKind ?? null,
            ),
        );
    }

    // ---- Step 3: pick variant template ----

    protected pickVariantTemplate(t: GameVariantTemplate): void {
        this.selectedVariantTemplate.set(t);
        this.variantDraft.set(this.makeVariantDraftFromTemplate(t));
        this.stepError.set(null);
        this.step.set(4);
    }

    protected trackVariantTemplate(
        _index: number,
        t: GameVariantTemplate,
    ): string {
        return t.GameVariantTemplateId;
    }

    protected async reloadVariantTemplates(): Promise<void> {
        const g = this.createdGame();
        if (!g) return;
        await this.variantsService.loadTemplates(this.realmId, g.Id);
    }

    // ---- Step 4: configure variant ----

    protected setVariantField<K extends keyof VariantDraft>(
        key: K,
        value: VariantDraft[K],
    ): void {
        this.variantDraft.update((d) => ({ ...d, [key]: value }));
    }

    protected variantPreviewName(t: GameVariantTemplate): string {
        const d = this.variantDraft();
        return d.overrideDisplayInfo ? d.displayName : t.DisplayInfo.Name;
    }

    protected variantPreviewDescription(t: GameVariantTemplate): string {
        const d = this.variantDraft();
        return d.overrideDisplayInfo
            ? d.displayDescription
            : (t.DisplayInfo.Description ?? '');
    }

    protected backToStep3(): void {
        if (this.working()) return;
        this.selectedVariantTemplate.set(null);
        this.stepError.set(null);
        this.step.set(3);
    }

    protected async createVariantAndFinish(): Promise<void> {
        const t = this.selectedVariantTemplate();
        const g = this.createdGame();
        if (!t || !g || this.working() || !this.canSaveVariantStep()) return;
        const d = this.variantDraft();
        const displayInfo: DisplayInfo | null = d.overrideDisplayInfo
            ? {
                  Name: d.displayName,
                  Description: d.displayDescription,
                  ImageUrl: d.displayImageUrl,
              }
            : null;
        const isActive = d.overrideIsActive ? d.isActive : null;

        this.working.set(true);
        this.stepError.set(null);
        try {
            const result = await this.variantsService.createVariant({
                RealmId: this.realmId,
                GameId: g.Id,
                GameVariantTemplateId: t.GameVariantTemplateId,
                IsActive: isActive,
                DisplayInfo: displayInfo,
            });
            if (result.kind === 'ok') {
                // Game + variant exist; offer the optional seed-reviews step.
                this.created.emit();
                this.initSeedStep();
                this.step.set(5);
            } else {
                this.stepError.set(
                    this.formatCreateVariantError(result.reason),
                );
            }
        } finally {
            this.working.set(false);
        }
    }

    // ---- Step 5: seed fake reviews (optional) ----

    private initSeedStep(): void {
        this.seedTargetAverage.set(4.5);
        this.seedCount.set(10);
        this.seedConcentration.set(1);
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - SEED_RANGE_DAYS);
        this.seedFrom.set(from);
        this.seedTo.set(to);
        this.seedError.set(null);
    }

    protected async seedReviews(): Promise<void> {
        const g = this.createdGame();
        if (!g || this.working() || this.seedDisabled()) return;
        this.working.set(true);
        this.seedError.set(null);
        try {
            const outcome = await this.fakeReviewService.addGameReviews({
                realmId: this.realmId,
                gameId: g.Id,
                ratingCounts: buildBellDistribution(
                    this.seedTargetAverage(),
                    this.seedCount(),
                    this.seedConcentration(),
                ),
                tags: this.seedTags(),
                createdFrom: (this.seedFrom() ?? new Date()).toISOString(),
                createdTo: (this.seedTo() ?? new Date()).toISOString(),
            });
            if (outcome.result === 'Ok') {
                this.notification.show({
                    severity: 'success',
                    summary: 'Reviews seeded',
                    detail: `Created ${outcome.createdCount}.`,
                });
                this.close();
            } else if (outcome.result === 'InsufficientAuthors') {
                this.seedError.set(
                    `Not enough fake authors: need ${outcome.requestedAuthors}, available ${outcome.availableAuthors}. Add more authors first, or skip.`,
                );
            } else if (outcome.result === 'InsufficientComments') {
                this.seedError.set(
                    `Not enough ${outcome.commentsRating}★ comments matching the tags: need ${outcome.requestedComments}, available ${outcome.availableComments}. Add comments, or skip.`,
                );
            } else {
                this.seedError.set(
                    'Failed to seed reviews. You can skip and add them later.',
                );
            }
        } finally {
            this.working.set(false);
        }
    }

    protected skipSeed(): void {
        if (this.working()) return;
        this.close();
    }

    // ---- Dialog lifecycle ----

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
        if (!value) {
            this.resetAll();
        }
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    private resetAll(): void {
        this.step.set(1);
        this.selectedGameTemplate.set(null);
        this.gameDraft.set(this.makeEmptyGameDraft());
        this.createdGame.set(null);
        this.selectedVariantTemplate.set(null);
        this.variantDraft.set(this.makeEmptyVariantDraft());
        this.stepError.set(null);
        this.seedError.set(null);
        this.working.set(false);
    }

    private gameTemplateFieldValue<K extends keyof GameDraft>(
        wrap: GameTemplateWithStatus,
        key: K,
    ): GameDraft[K]['value'] {
        const t = wrap.Template;
        switch (key) {
            case 'Title':
                return t.Title as GameDraft[K]['value'];
            case 'Description':
                return t.Description as GameDraft[K]['value'];
            case 'IsActive':
                return t.IsActive as GameDraft[K]['value'];
            case 'IsFeatured':
                return t.IsFeatured as GameDraft[K]['value'];
        }
        return '' as GameDraft[K]['value'];
    }

    private makeEmptyGameDraft(): GameDraft {
        const empty: OverrideField<string> = { override: false, value: '' };
        const emptyBool: OverrideField<boolean> = {
            override: false,
            value: false,
        };
        return {
            Title: { ...empty },
            Description: { ...empty },
            IsActive: { ...emptyBool },
            IsFeatured: { ...emptyBool },
        };
    }

    private makeEmptyVariantDraft(): VariantDraft {
        return {
            overrideDisplayInfo: false,
            displayName: '',
            displayDescription: '',
            displayImageUrl: '',
            overrideIsActive: false,
            isActive: true,
        };
    }

    private makeVariantDraftFromTemplate(t: GameVariantTemplate): VariantDraft {
        const draft: VariantDraft = {
            overrideDisplayInfo: false,
            displayName: t.DisplayInfo.Name ?? '',
            displayDescription: t.DisplayInfo.Description ?? '',
            displayImageUrl: t.DisplayInfo.ImageUrl ?? '',
            overrideIsActive: false,
            isActive: true,
        };
        // Pre-fill DisplayInfo override from the game's Title override so the
        // variant inherits the realm-specific game name on the first save.
        const titleOverride = this.gameTitleOverrideForVariantPrefill();
        if (titleOverride !== null && titleOverride.trim()) {
            draft.overrideDisplayInfo = true;
            draft.displayName = titleOverride;
        }
        return draft;
    }

    private gameTitleOverrideForVariantPrefill(): string | null {
        const g = this.createdGame();
        if (g) return g.Overrides.Title ?? null;
        const d = this.gameDraft().Title;
        return d.override ? d.value : null;
    }

    private formatCreateGameError(reason: string): string {
        switch (reason) {
            case 'TemplateNotFound':
                return 'Template not found.';
            case 'GameKindNotFound':
                return 'Template references a game kind that is not registered on the game server.';
            case 'GameKindAlreadyUsed':
                return 'This realm already has a game with the same game kind.';
            default:
                return 'Failed to create game. Please try again.';
        }
    }

    private formatCreateVariantError(reason: string): string {
        switch (reason) {
            case 'GameNotFound':
                return 'Game not found.';
            default:
                return 'Failed to create variant. The game was created — you can add a variant later from the Variants dialog.';
        }
    }
}
